/**
 * 근거 꼬리표(E5). 결과 숫자마다 어디서 온 값인지와 확인 상태를 붙인다.
 * 6종은 기능분류 RC-5(명세 P1-03)의 목록이다. 엔진이 계산한 값은 'calculated' 이고,
 * 입력에 추정(ai_estimated)이 섞이면 계산값이라도 'unconfirmed'(화면의 '미확인')다.
 */
export const FOOD_ENGINE_VERSION = 'food-engine-v1' as const

export const SOURCE_TYPES = [
  'user_input',
  'calculated',
  'ai_estimated',
  'measured',
  'sensory_panel',
  'supplier_quote',
] as const
export type SourceType = (typeof SOURCE_TYPES)[number]

/** confirmed: 확인된 입력만으로 계산 / unconfirmed: 추정 입력이 섞임 / held: 보류(value 는 null) */
export type ValueStatus = 'confirmed' | 'unconfirmed' | 'held'
export interface EngineIssue {
  code: string
  field: string
}
export interface EngineValue<T> {
  value: T | null
  status: ValueStatus
  sourceType: SourceType
  issues: EngineIssue[]
  engineVersion: typeof FOOD_ENGINE_VERSION
}

/** 확인된 근거로 보는 출처. 추정과 출처 없음(undefined)은 미확인 */
const CONFIRMED_SOURCES: ReadonlySet<SourceType> = new Set([
  'user_input',
  'measured',
  'supplier_quote',
  'calculated',
])
export const isConfirmedSource = (source: SourceType | undefined) =>
  source !== undefined && CONFIRMED_SOURCES.has(source)

/** 계산값 꼬리표. 이슈가 있으면 보류, 아니면 입력 출처들로 확인 상태를 정한다 */
export function calculated<T>(
  value: T | null,
  issues: EngineIssue[],
  sources: ReadonlyArray<SourceType | undefined>,
): EngineValue<T> {
  const held = value === null || issues.length > 0
  return {
    value: held ? null : value,
    status: held ? 'held' : sources.every(isConfirmedSource) ? 'confirmed' : 'unconfirmed',
    sourceType: 'calculated',
    issues,
    engineVersion: FOOD_ENGINE_VERSION,
  }
}
export const held = <T>(code: string, field: string): EngineValue<T> =>
  calculated<T>(null, [{ code, field }], [])

// ---- 단가 근거 (기존 RecipeCat cost-evidence 의 계산부를 옮겼다. 저장된 결과의 칸 값이라 이름·값을 바꾸지 않는다) ----

/** Public metadata only. No supplier evidence, URLs, contacts or validation claims. */
export const PRICE_EVIDENCE_SOURCES = [
  'unknown',
  'model_estimate',
  'search_estimate',
  'user_input',
] as const
export const PRICE_EVIDENCE_STATUSES = ['missing', 'estimated', 'provided'] as const
export interface PriceEvidence {
  source: (typeof PRICE_EVIDENCE_SOURCES)[number]
  status: (typeof PRICE_EVIDENCE_STATUSES)[number]
}
export const MISSING_PRICE: PriceEvidence = { source: 'unknown', status: 'missing' }
export const USER_PRICE: PriceEvidence = { source: 'user_input', status: 'provided' }

/** A positive legacy number is an estimate, never a verified price. */
export function priceEvidence(value: number, evidence?: PriceEvidence): PriceEvidence {
  if (!Number.isFinite(value) || value < 0 || evidence?.status === 'missing')
    return { ...MISSING_PRICE }
  if (evidence?.source === 'user_input' && evidence.status === 'provided') return { ...USER_PRICE }
  if (value === 0) return { ...MISSING_PRICE }
  // user_input 은 estimated 로도 남긴다: 분량 확인 전 사용자 단가(qtyCheckedEvidence)가 확인 뒤 다시 provided 가 되게
  return {
    source: evidence?.status === 'estimated' ? evidence.source : 'unknown',
    status: 'estimated',
  }
}

/**
 * 기존 단가 근거 → E5 출처. search_estimate 는 Perplexity 웹 검색 시세(LLM 이 찾은 값)라 ai_estimated 다.
 * 분량 확인 전 사용자 단가(user_input·estimated)도 단가 출처는 사용자다 — 분량의 확인 상태는 수량 쪽(normalizeQuantity)이 따로 말한다.
 * 단가 없음은 null(출처 없음).
 */
export function priceSourceType(evidence: PriceEvidence): SourceType | null {
  if (evidence.status === 'missing') return null
  return evidence.source === 'user_input' ? 'user_input' : 'ai_estimated'
}
